import { useEffect } from 'react'
import { Article } from '@app-core/eventTracking/article/Article'
import { FollowUIEvents, LikeUIEvents, NavigationEvents } from '@libs/customEvents'
import {
  FollowActionType,
  LikeActionType,
  trackArticleInfoFollowEvent,
  UserProfileActionType,
  trackArticleInfoUserProfileEvent,
  trackArticleInfoEnterPlaceEvent,
  trackArticleReadMoreSameTypeLikeEvent,
  trackArticleReadMoreSameAuthorEnterEvent,
  trackArticleReadMoreSameAuthorLikeEvent,
  trackArticleReadMoreSameTypeEnterEvent,
} from '@libs/eventTracking/article'
import { TrackingSection } from '@features/post/contexts/TrackingSectionContext'

type TrackingSectionInfo = { section: TrackingSection }

export function useTrackArticleInfoEvent(article: Article) {
  useEffect(() => {
    const handler = ({
      detail: { actionType, section },
    }: CustomEventInit<{ actionType: FollowActionType } & TrackingSectionInfo>) => {
      if (section !== TrackingSection.ARTICLE_INFO) return
      trackArticleInfoFollowEvent(article, actionType)
    }
    window.addEventListener(FollowUIEvents.CLICK_FOLLOW, handler)
    return () => {
      window.removeEventListener(FollowUIEvents.CLICK_FOLLOW, handler)
    }
  }, [])
  useEffect(() => {
    const handler = ({
      detail: { actionType, section },
    }: CustomEventInit<{ actionType: UserProfileActionType } & TrackingSectionInfo>) => {
      if (section !== TrackingSection.ARTICLE_INFO) return
      trackArticleInfoUserProfileEvent(article, actionType)
    }
    window.addEventListener(NavigationEvents.ENTER_USER_PROFILE, handler)
    return () => {
      window.removeEventListener(NavigationEvents.ENTER_USER_PROFILE, handler)
    }
  }, [])
  useEffect(() => {
    const handler = ({
      detail: { locationId, section },
    }: CustomEventInit<{ locationId: string } & TrackingSectionInfo>) => {
      if (section !== TrackingSection.ARTICLE_INFO) return
      trackArticleInfoEnterPlaceEvent(article, `/${locationId.replace('.', '/')}`)
    }
    window.addEventListener(NavigationEvents.ENTER_PLACE, handler)
    return () => {
      window.removeEventListener(NavigationEvents.ENTER_PLACE, handler)
    }
  }, [])
  useEffect(() => {
    const handler = ({
      detail: { actionType },
    }: CustomEventInit<{ actionType: LikeActionType } & TrackingSectionInfo>) => {
      trackArticleReadMoreSameTypeLikeEvent(article, actionType)
    }
    window.addEventListener(LikeUIEvents.READ_MORE_SANE_TYPE_CLICK_LIKE, handler)
    return () => {
      window.removeEventListener(LikeUIEvents.READ_MORE_SANE_TYPE_CLICK_LIKE, handler)
    }
  }, [])
  useEffect(() => {
    const handler = (event: CustomEventInit<{ toPath: string }>) => {
      const {
        detail: { toPath },
      } = event
      trackArticleReadMoreSameAuthorEnterEvent(article, toPath)
    }
    window.addEventListener(NavigationEvents.ENTER_READ_MORE_SAME_AUTHOR, handler)
    return () => {
      window.removeEventListener(NavigationEvents.ENTER_READ_MORE_SAME_AUTHOR, handler)
    }
  }, [])
  useEffect(() => {
    const handler = ({
      detail: { actionType },
    }: CustomEventInit<{ actionType: LikeActionType } & TrackingSectionInfo>) => {
      trackArticleReadMoreSameAuthorLikeEvent(article, actionType)
    }
    window.addEventListener(LikeUIEvents.READ_MORE_SANE_AUTHOR_CLICK_LIKE, handler)
    return () => {
      window.removeEventListener(LikeUIEvents.READ_MORE_SANE_AUTHOR_CLICK_LIKE, handler)
    }
  }, [])
  useEffect(() => {
    const handler = (event: CustomEventInit<{ toPath: string }>) => {
      const {
        detail: { toPath },
      } = event
      trackArticleReadMoreSameTypeEnterEvent(article, toPath)
    }
    window.addEventListener(NavigationEvents.ENTER_READ_MORE_SAME_TYPE, handler)
    return () => {
      window.removeEventListener(NavigationEvents.ENTER_READ_MORE_SAME_TYPE, handler)
    }
  }, [])
}
